import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Link } from 'react-router-dom';
import AulaUserContext, { aulaAuthHeaders } from '../aulaUserContext';

const CONDICIONES_IVA = ['Consumidor Final', 'Responsable Inscripto', 'Monotributista', 'Exento'];

// Datos para que el admin arme la factura manualmente — se piden obligatorios al solicitar
// el curso (la facturación en sí sigue siendo manual y por fuera del sitio).
// Sanitización básica en el cliente (además de la que hace el backend al guardar):
// quita ángulos (HTML) y caracteres de control, y limita longitud.
const esControl = (codigo) => codigo < 32 || codigo === 127;
const limpiarTexto = (valor, maxLen) => valor
    .split('')
    .filter((ch) => ch !== '<' && ch !== '>' && !esControl(ch.charCodeAt(0)))
    .join('')
    .slice(0, maxLen);

const limpiarDniCuit = (valor) => valor.replace(/[^0-9-]/g, '').slice(0, 13);

const DatosFacturacionForm = ({ onConfirmar, textoBoton }) => {
    const [cuit, setCuit] = useState('');
    const [razonSocial, setRazonSocial] = useState('');
    const [condicionIva, setCondicionIva] = useState(CONDICIONES_IVA[0]);
    const [domicilioFiscal, setDomicilioFiscal] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        onConfirmar({
            cuit: cuit.trim(),
            razonSocial: razonSocial.trim(),
            condicionIva,
            domicilioFiscal: domicilioFiscal.trim()
        });
    };

    return (
        <form onSubmit={handleSubmit} className="aula-datos-facturacion">
            <p style={{ fontWeight: 'bold', color: '#000' }}>Datos para la factura:</p>
            <div className="form-group">
                <label>DNI / CUIT / CUIL</label>
                <input className="form-control" value={cuit}
                    onChange={(e) => setCuit(limpiarDniCuit(e.target.value))} required />
            </div>
            <div className="form-group">
                <label>Condición frente al IVA</label>
                <select className="form-control" value={condicionIva} onChange={(e) => setCondicionIva(e.target.value)}>
                    {CONDICIONES_IVA.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>
            <div className="form-group">
                <label>Razón social / nombre para la factura (opcional)</label>
                <input className="form-control" value={razonSocial}
                    onChange={(e) => setRazonSocial(limpiarTexto(e.target.value, 150))} />
            </div>
            <div className="form-group">
                <label>Domicilio fiscal (opcional)</label>
                <input className="form-control" value={domicilioFiscal}
                    onChange={(e) => setDomicilioFiscal(limpiarTexto(e.target.value, 200))} />
            </div>
            <button className="btn aula-btn-primary" type="submit">{textoBoton}</button>
        </form>
    );
};

const CursoDetalle = (props) => {
    const cursoId = props.match.params.id;
    const { aulaAuth } = useContext(AulaUserContext);
    const [curso, setCurso] = useState(null);
    const [inscripcion, setInscripcion] = useState(null);
    const [progreso, setProgreso] = useState(null);
    const [mensaje, setMensaje] = useState('');
    const [subiendoComprobante, setSubiendoComprobante] = useState(false);
    const [errorComprobante, setErrorComprobante] = useState('');

    const cargarTodo = useCallback(() => {
        fetch(`/aula/cursos/${cursoId}`, { headers: aulaAuthHeaders() })
            .then((res) => res.json())
            .then(setCurso);

        if (aulaAuth.token) {
            fetch('/aula/inscripciones/mias', { headers: aulaAuthHeaders() })
                .then((res) => res.json())
                .then((lista) => setInscripcion(lista.find((i) => i.cursoId === Number(cursoId)) || null));
        }
    }, [cursoId, aulaAuth.token]);

    useEffect(() => { cargarTodo(); }, [cargarTodo]);

    const activo = inscripcion && inscripcion.estado === 'Activo' && inscripcion.vigente;

    useEffect(() => {
        if (activo) {
            fetch(`/aula/progreso/${cursoId}`, { headers: aulaAuthHeaders() })
                .then((res) => res.json())
                .then(setProgreso);
        }
    }, [activo, cursoId]);

    const handleInscribirse = (datosFacturacion) => {
        fetch(`/aula/inscripciones/${cursoId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json;charset=UTF-8', ...aulaAuthHeaders() },
            body: JSON.stringify(datosFacturacion)
        })
            .then((res) => res.json())
            .then(() => {
                setMensaje('Solicitud enviada. Un admin va a habilitar tu acceso una vez verificado el pago.');
                cargarTodo();
            });
    };

    const handleAdjuntarComprobante = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setErrorComprobante('');
        setSubiendoComprobante(true);

        const reader = new FileReader();
        reader.onload = () => {
            fetch(`/aula/inscripciones/${inscripcion.id}/comprobante`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json;charset=UTF-8', ...aulaAuthHeaders() },
                body: JSON.stringify({ archivoBase64: reader.result })
            }).then(async (res) => {
                setSubiendoComprobante(false);
                if (res.status === 200) {
                    cargarTodo();
                } else {
                    setErrorComprobante(await res.text());
                }
            });
        };
        reader.readAsDataURL(file);
    };

    const handleDescargarCertificado = () => {
        fetch(`/aula/certificado/${cursoId}`, { headers: aulaAuthHeaders() })
            .then((res) => res.blob())
            .then((blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `certificado-${curso.nombre}.pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
            });
    };

    if (!curso) return <div className="container my-4">Cargando...</div>;

    const pendiente = inscripcion && inscripcion.estado === 'PendientePago';
    // Baja manual, o Activo pero venció el plazo de acceso configurado en el curso: puede volver a pedir acceso.
    const puedeReSolicitar = inscripcion && (inscripcion.estado === 'Baja' ||
        (inscripcion.estado === 'Activo' && !inscripcion.vigente));

    return (
        <div className="container my-4">
            {curso.imagenUrl &&
                <img src={curso.imagenUrl} alt={curso.nombre} className="aula-curso-caratula-detalle" />}
            <h3 className="title">{curso.nombre}</h3>
            <p>{curso.descripcion}</p>

            {!aulaAuth.token &&
                <div>
                    <p><strong>${curso.precio}</strong> — pago por transferencia (fuera del sitio).</p>
                    <p>Iniciá sesión o creá una cuenta para inscribirte:</p>
                    <Link className="btn aula-btn-primary mr-2"
                        to={{ pathname: '/aula/login', state: { from: `/aula/curso/${cursoId}` } }}>
                        Iniciar sesión
                    </Link>
                    <Link className="btn aula-btn-outline"
                        to={{ pathname: '/aula/registro', state: { from: `/aula/curso/${cursoId}` } }}>
                        Registrarme
                    </Link>
                </div>}

            {aulaAuth.token && !inscripcion &&
                <div>
                    <p><strong>${curso.precio}</strong> — pago por transferencia (fuera del sitio).</p>
                    <DatosFacturacionForm onConfirmar={handleInscribirse} textoBoton="Inscribirme" />
                </div>}

            {pendiente &&
                <div>
                    <p className="text-warning">Tu inscripción está pendiente de pago. Te vamos a habilitar el acceso apenas verifiquemos el comprobante.</p>
                    <p>
                        Ya te enviamos los datos de pago por mail. Si querés, también podés adjuntar acá el
                        comprobante para que lo validemos más rápido (no reemplaza el mail, es opcional):
                    </p>
                    {inscripcion.comprobanteUrl
                        ? <p className="text-success">Comprobante recibido ✓ (podés volver a adjuntar otro si hace falta)</p>
                        : null}
                    <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={handleAdjuntarComprobante} disabled={subiendoComprobante} />
                    {subiendoComprobante && <span className="d-block text-muted">Subiendo...</span>}
                    {errorComprobante && <span className="d-block text-danger">{errorComprobante}</span>}
                </div>}

            {puedeReSolicitar &&
                <div>
                    <p className="text-warning">
                        {inscripcion.estado === 'Baja'
                            ? 'Tu acceso a este curso fue dado de baja.'
                            : 'Tu acceso a este curso venció.'}
                    </p>
                    <DatosFacturacionForm onConfirmar={handleInscribirse} textoBoton="Volver a solicitar acceso" />
                </div>}

            {mensaje && <p className="text-success">{mensaje}</p>}

            {activo &&
                <div>
                    {progreso &&
                        <div className="progress mb-3" style={{ height: '24px' }}>
                            <div className="progress-bar aula-progress-bar" style={{ width: `${progreso.porcentaje}%` }}>
                                {progreso.porcentaje}%
                            </div>
                        </div>}

                    <h5 className="mt-4">Temario</h5>
                    <div className="list-group">
                        {curso.modulos.map((modulo) => {
                            const estadoModulo = progreso && progreso.modulos.find((m) => m.id === modulo.id);
                            const completado = estadoModulo && estadoModulo.completado;
                            return (
                                <Link
                                    to={`/aula/curso/${cursoId}/modulo/${modulo.id}`}
                                    key={modulo.id}
                                    className={`list-group-item aula-modulo-list-item ${completado ? 'completado' : ''}`}
                                >
                                    <span>{modulo.orden}. {modulo.titulo}</span>
                                    {completado && <span className="aula-modulo-check">Completado ✓</span>}
                                </Link>
                            );
                        })}
                    </div>

                    {progreso && progreso.completo &&
                        <button className="btn aula-btn-primary mt-4" onClick={handleDescargarCertificado}>
                            Descargar certificado
                        </button>}
                </div>}
        </div>
    );
};

export default CursoDetalle;
